import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Input,
  VStack,
  Text,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Spinner,
  useToast,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import axios from 'axios';
import debounce from 'lodash/debounce';
import FundCharts from './FundCharts';

// Update the axios configuration
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

const FundSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statsBgColor = useColorModeValue('gray.50', 'gray.700');

  const fetchFunds = useCallback(async (query) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/mutual-funds/search?query=${query}`);
      setFunds(response.data);
    } catch (error) {
      toast({
        title: 'Error fetching funds',
        description: error.response?.data?.message || 'Failed to fetch mutual funds',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      fetchFunds(value);
    }, 500),
    [fetchFunds]
  );

  useEffect(() => {
    fetchFunds('');
    return () => {
      debouncedSearch.cancel();
    };
  }, [fetchFunds, debouncedSearch]);

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleFundSelect = async (fund) => {
    setSelectedFund(fund);
    setIsDrawerOpen(true);
  };

  const formatNumber = (num) => {
    if (num === 'N/A') return num;
    return parseFloat(num).toFixed(2);
  };

  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search mutual funds by name, type, or fund house..."
            value={searchTerm}
            onChange={handleSearch}
            bg={bgColor}
            borderColor={borderColor}
          />
        </InputGroup>

        {loading ? (
          <Box textAlign="center" py={4}>
            <Spinner size="lg" />
          </Box>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Fund Name</Th>
                  <Th>Fund House</Th>
                  <Th>Category</Th>
                  <Th>NAV</Th>
                  <Th>1Y Returns</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {funds.map((fund) => (
                  <Tr key={fund.schemeCode}>
                    <Td>
                      <Text fontWeight="medium">{fund.schemeName}</Text>
                      <Badge colorScheme="blue" mt={1}>{fund.schemeType}</Badge>
                    </Td>
                    <Td>{fund.fundHouse}</Td>
                    <Td>{fund.category}</Td>
                    <Td>₹{formatNumber(fund.nav)}</Td>
                    <Td color={fund.returns['1Y'] > 0 ? 'green.500' : 'red.500'}>
                      {fund.returns['1Y']}
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => handleFundSelect(fund)}
                      >
                        View Details
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        <Drawer
          isOpen={isDrawerOpen}
          placement="right"
          onClose={() => setIsDrawerOpen(false)}
          size="md"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>Fund Details</DrawerHeader>
            <DrawerBody>
              {selectedFund && (
                <VStack align="stretch" spacing={6}>
                  <Box>
                    <Text fontSize="2xl" fontWeight="bold">
                      {selectedFund.schemeName}
                    </Text>
                    <HStack spacing={4} mt={2} wrap="wrap">
                      <Badge colorScheme="blue">{selectedFund.schemeType}</Badge>
                      <Text fontSize="sm">Fund House: {selectedFund.fundHouse}</Text>
                    </HStack>
                  </Box>

                  <Box p={4} bg={statsBgColor} borderRadius="md">
                    <SimpleGrid columns={2} spacing={4}>
                      <Stat>
                        <StatLabel>Current NAV</StatLabel>
                        <StatNumber>₹{formatNumber(selectedFund.nav)}</StatNumber>
                        <Text fontSize="sm" color="gray.500">
                          As of {selectedFund.lastUpdated}
                        </Text>
                      </Stat>
                      <Stat>
                        <StatLabel>Category</StatLabel>
                        <StatNumber fontSize="xl">{selectedFund.category}</StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>

                  <Box>
                    <Text fontSize="xl" fontWeight="semibold" mb={4}>
                      Performance
                    </Text>
                    <SimpleGrid columns={3} spacing={4}>
                      <Stat>
                        <StatLabel>1 Year Returns</StatLabel>
                        <StatNumber color={selectedFund.returns['1Y'] > 0 ? 'green.500' : 'red.500'}>
                          {selectedFund.returns['1Y']}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>3 Year Returns</StatLabel>
                        <StatNumber color={selectedFund.returns['3Y'] > 0 ? 'green.500' : 'red.500'}>
                          {selectedFund.returns['3Y']}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>5 Year Returns</StatLabel>
                        <StatNumber color={selectedFund.returns['5Y'] > 0 ? 'green.500' : 'red.500'}>
                          {selectedFund.returns['5Y']}
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>

                  <FundCharts fund={selectedFund} />
                </VStack>
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </VStack>
    </Box>
  );
};

export default FundSearch;
